/**
 * ehr#565 - authorizations used / remaining, the web side. The API counts used from the client's
 * checked-out sessions (apps/api/src/modules/clinical/auth-usage.ts); these helpers only display it.
 */

/** How many an authorization allows: Sessions Authorized first, else the number in Units. */
export function authAllowed(sessions: string, units: string): number | null {
  if (/^\d+$/.test(sessions.trim())) return Number(sessions.trim());
  const m = /\d+/.exec(units.replace(/,/g, ''));
  return m ? Number(m[0]) : null;
}

/** Running low (shown red): 2 or fewer left, or a fifth or less of what was authorized. */
export function authLow(r: { authorized: number | null; remaining: number | null }): boolean {
  return (
    r.remaining != null &&
    r.authorized != null &&
    r.remaining <= Math.max(2, Math.floor(r.authorized * 0.2))
  );
}

/** The chart deep link: the client's drawer on its Authorizations tab. */
export const authChartHref = (contactId: string) =>
  `/?contact=${encodeURIComponent(contactId)}&view=authorizations`;

/** YYYY-MM-DD → MM/DD/YYYY, as the chart shows dates. */
export const usDay = (d: string) => `${d.slice(5, 7)}/${d.slice(8, 10)}/${d.slice(0, 4)}`;

/** One pick-list option, as the Dd / MultiDd drop-downs take it. */
export interface AuthOption {
  value: string;
  label: string;
  search?: string;
}

/** "F33.1, F41.1" or ["90837 ", ""] → the codes, trimmed, blanks and repeats dropped. */
export function splitCodes(v: string | string[] | null | undefined): string[] {
  const parts = Array.isArray(v) ? v : (v ?? '').split(',');
  const out: string[] = [];
  for (const p of parts) {
    const c = p.trim();
    if (c && !out.includes(c)) out.push(c);
  }
  return out;
}

/** A code already on the authorization that the list no longer has stays pickable, flagged. */
const keepSaved = (opts: AuthOption[], picked: string[], note: string): AuthOption[] => [
  ...opts,
  ...picked
    .filter((c) => !opts.some((o) => o.value === c))
    .map((c) => ({ value: c, label: `${c} (${note})` })),
];

/**
 * ehr#565 (Chase 10-25): CPT Codes picks from the practice's one code list, the Calendar's
 * chargemaster (GET /cpt-codes, the list the Calendar's appointment types and Settings > CPT codes
 * use). Turned-off codes are left out unless the authorization already has them.
 */
export function cptOptions(
  codes: Array<{ code: string; shortDescription: string; active: boolean }>,
  picked: string[],
): AuthOption[] {
  const opts = codes
    .filter((c) => c.active || picked.includes(c.code))
    .map((c) => ({
      value: c.code,
      label: c.shortDescription ? `${c.code} · ${c.shortDescription}` : c.code,
      search: c.code,
    }));
  return keepSaved(opts, picked, 'not in the CPT code list');
}

/**
 * ehr#565: Diagnosis picks from the ICD-10 list the progress note's Diagnosis window uses (one
 * catalog for every picker, ICD10_CATALOG, since the ehr#741 follow-up).
 */
export function dxOptions(
  library: readonly (readonly [string, string])[],
  picked: string[],
): AuthOption[] {
  const opts = library.map(([code, label]) => ({
    value: code,
    label: `${code} · ${label}`,
    search: code,
  }));
  return keepSaved(opts, picked, 'not in the ICD-10 list');
}

/**
 * ehr#565: Payor picks from RCM's payors, the list Insurance / Payor and the Calendar use. An
 * authorization stores the payor's name, so a name RCM has once per plan state is listed once. A
 * name saved before (typed by hand) that RCM doesn't have stays, flagged.
 */
export function authPayorOptions(known: Array<{ name: string }>, saved: string): AuthOption[] {
  const names: string[] = [];
  for (const p of known) {
    const n = p.name.trim();
    if (n && !names.some((x) => x.toLowerCase() === n.toLowerCase())) names.push(n);
  }
  const opts = names.map((n) => ({ value: n, label: n }));
  const s = saved.trim();
  return s && !names.includes(s)
    ? [...opts, { value: s, label: `${s} (not in RCM payors)` }]
    : opts;
}

/** The fields an authorization can't be saved without, as the form names them. */
export const AUTH_REQUIRED = { authNo: 'Auth Number', payer: 'Payor' } as const;
export type AuthRequiredField = keyof typeof AUTH_REQUIRED;

/** ehr#565 (Chase 10-25): which required fields are still empty, in form order. */
export function missingAuthFields(v: Record<AuthRequiredField, string>): AuthRequiredField[] {
  return (Object.keys(AUTH_REQUIRED) as AuthRequiredField[]).filter((k) => !v[k].trim());
}

/** "Fill in Auth Number and Payor to save." */
export function missingAuthMessage(missing: AuthRequiredField[]): string {
  const names = missing.map((k) => AUTH_REQUIRED[k]);
  return names.length ? `Fill in ${names.join(' and ')} to save.` : '';
}
